import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import {
  DeleteCommand,
  DynamoDBDocumentClient,
  GetCommand,
  PutCommand,
  UpdateCommand,
} from "@aws-sdk/lib-dynamodb";
import { MemoryStore, type Store } from "hono-rate-limiter";

export interface RateLimitCommandClient {
  send(command: { input: unknown }): Promise<unknown>;
}

export interface DynamoDbRateLimitStoreOptions {
  client: RateLimitCommandClient;
  tableName: string;
  keyPrefix?: string;
  windowMs: number;
  now?: () => number;
}

type RateLimitItem = {
  key: string;
  hitCount: number;
  expiresAt: number;
  windowStart: number;
};

type RateLimitInfo = {
  totalHits: number;
  resetTime?: Date;
};

const DEFAULT_KEY_PREFIX = "mcp-server";

export class DynamoDbRateLimitStore implements Store {
  readonly localKeys = false;
  readonly prefix: string;

  private readonly client: RateLimitCommandClient;
  private readonly tableName: string;
  private readonly windowMs: number;
  private readonly now: () => number;

  constructor(options: DynamoDbRateLimitStoreOptions) {
    this.client = options.client;
    this.tableName = options.tableName;
    this.prefix = options.keyPrefix ?? DEFAULT_KEY_PREFIX;
    this.windowMs = options.windowMs;
    this.now = options.now ?? Date.now;
  }

  init(): void {}

  async get(key: string): Promise<RateLimitInfo | undefined> {
    const now = this.now();
    const currentWindowStart = this.windowStart(now);
    const item = (await this.client.send(
      new GetCommand({
        TableName: this.tableName,
        Key: { key: this.storageKey(key) },
      }),
    )) as { Item?: RateLimitItem };

    if (!item.Item || item.Item.windowStart !== currentWindowStart) {
      return undefined;
    }

    return {
      totalHits: item.Item.hitCount,
      resetTime: new Date(item.Item.expiresAt * 1000),
    };
  }

  async increment(key: string): Promise<RateLimitInfo> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const now = this.now();
      const currentWindowStart = this.windowStart(now);
      const expiresAt = Math.ceil((currentWindowStart + this.windowMs) / 1000);
      const storageKey = this.storageKey(key);

      const resetWindowResult = await this.tryResetWindow({
        storageKey,
        currentWindowStart,
        expiresAt,
      });
      if (resetWindowResult) {
        return resetWindowResult;
      }

      const result = await this.tryIncrementWithinWindow({
        storageKey,
        currentWindowStart,
        expiresAt,
      });
      if (result) {
        return result;
      }
    }

    throw new Error("Failed to increment DynamoDB rate-limit counter");
  }

  async decrement(key: string): Promise<void> {
    try {
      const currentWindowStart = this.windowStart(this.now());
      await this.client.send(
        new UpdateCommand({
          TableName: this.tableName,
          Key: { key: this.storageKey(key) },
          UpdateExpression: "ADD hitCount :decrement",
          ConditionExpression:
            "attribute_exists(#key) AND windowStart = :windowStart AND hitCount > :zero",
          ExpressionAttributeNames: {
            "#key": "key",
          },
          ExpressionAttributeValues: {
            ":decrement": -1,
            ":zero": 0,
            ":windowStart": currentWindowStart,
          },
        }),
      );
    } catch {
      // Best-effort only; hono-rate-limiter can call this when requests are skipped.
    }
  }

  async resetKey(key: string): Promise<void> {
    await this.client.send(
      new DeleteCommand({
        TableName: this.tableName,
        Key: { key: this.storageKey(key) },
      }),
    );
  }

  private storageKey(key: string): string {
    return `${this.prefix}:${key}`;
  }

  private windowStart(now: number): number {
    return Math.floor(now / this.windowMs) * this.windowMs;
  }

  private async tryResetWindow(params: {
    storageKey: string;
    currentWindowStart: number;
    expiresAt: number;
  }): Promise<RateLimitInfo | undefined> {
    try {
      await this.client.send(
        new PutCommand({
          TableName: this.tableName,
          Item: {
            key: params.storageKey,
            hitCount: 1,
            expiresAt: params.expiresAt,
            windowStart: params.currentWindowStart,
          },
          ConditionExpression:
            "attribute_not_exists(#key) OR windowStart <> :windowStart",
          ExpressionAttributeNames: {
            "#key": "key",
          },
          ExpressionAttributeValues: {
            ":windowStart": params.currentWindowStart,
          },
        }),
      );

      return {
        totalHits: 1,
        resetTime: new Date(params.expiresAt * 1000),
      };
    } catch (error) {
      if (!isConditionalCheckFailure(error)) {
        throw error;
      }

      return undefined;
    }
  }

  private async tryIncrementWithinWindow(params: {
    storageKey: string;
    currentWindowStart: number;
    expiresAt: number;
  }): Promise<RateLimitInfo | undefined> {
    try {
      const result = (await this.client.send(
        new UpdateCommand({
          TableName: this.tableName,
          Key: { key: params.storageKey },
          UpdateExpression:
            "SET expiresAt = :expiresAt ADD hitCount :increment",
          ConditionExpression: "windowStart = :windowStart",
          ExpressionAttributeValues: {
            ":expiresAt": params.expiresAt,
            ":increment": 1,
            ":windowStart": params.currentWindowStart,
          },
          ReturnValues: "ALL_NEW",
        }),
      )) as { Attributes?: RateLimitItem };

      const attributes = result.Attributes;
      if (!attributes) {
        throw new Error("Missing rate-limit attributes from DynamoDB update");
      }

      return {
        totalHits: attributes.hitCount,
        resetTime: new Date(attributes.expiresAt * 1000),
      };
    } catch (error) {
      if (!isConditionalCheckFailure(error)) {
        throw error;
      }

      return undefined;
    }
  }
}

function isConditionalCheckFailure(error: unknown): boolean {
  return (
    error instanceof Error && error.name === "ConditionalCheckFailedException"
  );
}

export interface CreateRateLimitStoreOptions {
  env: {
    NODE_ENV?: string;
    AWS_REGION?: string;
    RATE_LIMIT_TABLE_NAME?: string;
    RATE_LIMIT_KEY_PREFIX?: string;
  };
  windowMs: number;
}

export function createRateLimitStore(
  options: CreateRateLimitStoreOptions,
): Store {
  const { env, windowMs } = options;

  if (!env.RATE_LIMIT_TABLE_NAME || !env.AWS_REGION) {
    if (env.NODE_ENV === "production") {
      throw new Error(
        "RATE_LIMIT_TABLE_NAME and AWS_REGION are required in production",
      );
    }

    return new MemoryStore();
  }

  const client = DynamoDBDocumentClient.from(
    new DynamoDBClient({
      region: env.AWS_REGION,
    }),
  );

  return new DynamoDbRateLimitStore({
    client,
    tableName: env.RATE_LIMIT_TABLE_NAME,
    keyPrefix: env.RATE_LIMIT_KEY_PREFIX,
    windowMs,
  });
}
